import React, { useState, useEffect } from "react";
import AdminNav from "../components/admin/AdminNav";
import { CONTACTS as DEFAULT_CONTACTS } from "../data/content";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, Phone, MapPin, Clock, Mail, MessageCircle, Send } from "lucide-react";

export default function AdminSettings() {
  const [contacts, setContacts] = useState(DEFAULT_CONTACTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const res = await api.get("/site/content");
      if (res.data?.contacts && Object.keys(res.data.contacts).length > 0) {
        setContacts({ ...DEFAULT_CONTACTS, ...res.data.contacts });
      } else {
        setContacts(DEFAULT_CONTACTS);
      }
    } catch (err) {
      console.warn("Using default contacts:", err);
      setContacts(DEFAULT_CONTACTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleChange = (field, val) => {
    setContacts((prev) => ({ ...prev, [field]: val }));
  };

  const handleHourChange = (idx, field, val) => {
    const updatedHours = [...(contacts.hours || [])];
    updatedHours[idx] = { ...updatedHours[idx], [field]: val };
    setContacts((prev) => ({ ...prev, hours: updatedHours }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/content", { contacts });
      toast.success("Контакты и настройки сайта успешно обновлены!");
    } catch (err) {
      toast.error(formatApiError(err, "Ошибка при сохранении контактов"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE6] text-[#1A0E07]">
      <AdminNav />

      <main className="max-w-[1000px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b-2 border-[#4A1419]/20 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-5xl text-[#4A1419] font-bold">
              Контакты и график
            </h1>
            <p className="text-sm text-[#6E5549] mt-1">
              Настройки контактных данных, адреса мастерской и режима работы на сайте.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchContent}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium border-2 border-[#4A1419]/30 hover:border-[#4A1419] bg-[#F5EFE6] flex items-center gap-2"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              <span>Обновить</span>
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 text-sm font-bold bg-[#4A1419] text-[#F5EFE6] border-2 border-[#4A1419] hover:bg-[#320D10] flex items-center gap-2 shadow-md active:scale-95 transition-all"
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  <span>Сохранение...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Сохранить на сайте</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Settings Form */}
        <div className="space-y-6">
          {/* Main Info */}
          <div className="bg-white border-2 border-[#4A1419] p-6 shadow-sm">
            <h2 className="font-heading text-xl font-bold text-[#4A1419] mb-4 flex items-center gap-2">
              <Phone size={18} />
              <span>Телефоны и связь</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6E5549] mb-1">
                  Телефон для отображения на сайте
                </label>
                <input
                  type="text"
                  value={contacts.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="+7 910 235-87-58"
                  className="w-full px-3 py-2 border-2 border-[#4A1419]/20 focus:border-[#4A1419] text-sm font-medium focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6E5549] mb-1">
                  Номер для звонка (без пробелов)
                </label>
                <input
                  type="text"
                  value={contacts.phoneRaw || ""}
                  onChange={(e) => handleChange("phoneRaw", e.target.value)}
                  placeholder="+79102358758"
                  className="w-full px-3 py-2 border-2 border-[#4A1419]/20 focus:border-[#4A1419] text-sm font-medium focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6E5549] mb-1">
                  Email мастерской
                </label>
                <input
                  type="text"
                  value={contacts.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="info@shetka-shoes.ru"
                  className="w-full px-3 py-2 border-2 border-[#4A1419]/20 focus:border-[#4A1419] text-sm font-medium focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6E5549] mb-1">
                  Имя старшего мастера / представителя
                </label>
                <input
                  type="text"
                  value={contacts.master || ""}
                  onChange={(e) => handleChange("master", e.target.value)}
                  placeholder="Пётр Иванов"
                  className="w-full px-3 py-2 border-2 border-[#4A1419]/20 focus:border-[#4A1419] text-sm font-medium focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Address & Hours */}
          <div className="bg-white border-2 border-[#4A1419] p-6 shadow-sm">
            <h2 className="font-heading text-xl font-bold text-[#4A1419] mb-4 flex items-center gap-2">
              <MapPin size={18} />
              <span>Адрес и график работы</span>
            </h2>

            <div className="mb-4">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6E5549] mb-1">
                Адрес мастерской (текст на сайте)
              </label>
              <input
                type="text"
                value={contacts.address || ""}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="г. Брянск, ул. Объездная, 30 (1 этаж)"
                className="w-full px-3 py-2 border-2 border-[#4A1419]/20 focus:border-[#4A1419] text-sm font-medium focus:outline-none"
              />
            </div>

            <div className="space-y-3 pt-2">
              <span className="block text-xs font-bold uppercase tracking-wider text-[#6E5549]">
                Режим работы:
              </span>
              {contacts.hours?.map((h, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={h.day}
                    onChange={(e) => handleHourChange(i, "day", e.target.value)}
                    placeholder="Пн — Пт"
                    className="w-36 px-3 py-1.5 border border-[#4A1419]/30 text-sm focus:outline-none"
                  />
                  <span className="text-sm font-medium text-[#6E5549]">—</span>
                  <input
                    type="text"
                    value={h.time}
                    onChange={(e) => handleHourChange(i, "time", e.target.value)}
                    placeholder="10:00 — 20:00"
                    className="flex-1 px-3 py-1.5 border border-[#4A1419]/30 text-sm focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
